import { useState, type ReactNode } from "react";
import { useAuth } from "@tellescope/tanstack-query";
import { isDevelopment, isPreviewDeployment } from "@tellescope/utils";
import { useOrganization } from "../../shared/store/organizationStore";
import { AdminOrganizationMismatch } from "./AdminOrganizationMismatch";

interface AdminOrganizationGuardProps {
  children: ReactNode;
}

/**
 * Blocks the admin editor when the organization resolved from the URL is not the organization the
 * signed-in user belongs to. The editor loads its configuration from the URL's organization (a public
 * file) but saves to `session.userInfo.businessId`, so editing in this state silently copies another
 * organization's portal configuration onto your own.
 *
 * Local dev and PR previews can't resolve the real organization from the hostname (localhost matches on
 * customPortalURL, previews hard-code a subdomain), so they get a "Continue anyway" escape hatch.
 * Staging and production do not.
 */
export const AdminOrganizationGuard = ({ children }: AdminOrganizationGuardProps) => {
  const { session, logout } = useAuth();
  const organization = useOrganization();
  const [bypassed, setBypassed] = useState(false);

  // userInfo is empty until a session is established, so businessId can be missing at runtime
  const sessionBusinessId: string | undefined = session.userInfo.businessId;
  const urlBusinessId = organization?.businessId;

  if (!organization || !urlBusinessId || !sessionBusinessId || urlBusinessId === sessionBusinessId || bypassed) {
    return <>{children}</>;
  }

  const canBypass = isDevelopment() || isPreviewDeployment();

  return (
    <AdminOrganizationMismatch
      urlOrganizationName={organization.name || urlBusinessId}
      urlHost={window.location.host}
      sessionOrganizationName={session.userInfo.orgName || sessionBusinessId}
      onLogout={() => logout()}
      onContinueAnyway={canBypass ? () => setBypassed(true) : undefined}
    />
  );
};
